/**
 * useBrowserHeadsetDevice — v3.21-1753
 * ====================================
 * Lazy-loads and manages the ONE canonical browser Twilio Device for
 * the current authenticated operator + workstation.
 *
 * HARD RULES:
 *   • Never touches the network until Browser Headset mode is explicitly
 *     activated by the operator (`enable()`).
 *   • Never requests microphone permission when Browser Headset mode is
 *     off.
 *   • Never installs Twilio secrets in frontend code — the access token
 *     is fetched from the backend endpoint `/api/telephony/browser-token`
 *     which SHOULD be enabled server-side before any operator opts in.
 *   • Gracefully degrades: if `@twilio/voice-sdk` is not installed OR the
 *     backend token endpoint is not configured, `enable()` resolves to
 *     an error state ("Browser Headset Unavailable") without breaking
 *     Standard Calling.
 *   • Persists audio-device preferences (microphone / call output /
 *     ringtone) under `ptc.audio.*` in localStorage.
 */
import { useCallback, useRef, useState } from 'react';
import api from '../lib/api';
import { setActiveDevice, clearActiveDevice } from '../lib/browserVoice';
import { toast } from 'sonner';

const K_MIC     = 'ptc.audio.mic_device_id';
const K_OUTPUT  = 'ptc.audio.output_device_id';
const K_RINGER  = 'ptc.audio.ringer_device_id';

const STATE_IDLE       = 'idle';
const STATE_CONNECTING = 'connecting';
const STATE_ACTIVE     = 'active';
const STATE_ERROR      = 'error';

const KH122_HINT = /kh122m|prancybt/i;

function loadPref(k) {
  try { return window.localStorage.getItem(k) || null; } catch (_) { return null; }
}
function savePref(k, v) {
  try { window.localStorage.setItem(k, v || ''); } catch (_) {}
}

async function enumerateAudioDevices() {
  if (!navigator.mediaDevices?.enumerateDevices) return { inputs: [], outputs: [] };
  const all = await navigator.mediaDevices.enumerateDevices();
  return {
    inputs: all.filter(d => d.kind === 'audioinput'),
    outputs: all.filter(d => d.kind === 'audiooutput'),
  };
}

function pickPreferred(devices, savedId) {
  if (!devices || !devices.length) return null;
  if (savedId) {
    const hit = devices.find(d => d.deviceId === savedId);
    if (hit) return hit;
  }
  const kh = devices.find(d => KH122_HINT.test(d.label || ''));
  return kh || devices[0];
}

export function useBrowserHeadsetDevice() {
  const [state, setState] = useState(STATE_IDLE);
  const [error, setError] = useState('');
  const [inputs, setInputs] = useState([]);
  const [outputs, setOutputs] = useState([]);
  const [selected, setSelected] = useState({
    mic:    loadPref(K_MIC),
    output: loadPref(K_OUTPUT),
    ringer: loadPref(K_RINGER),
  });
  const deviceRef = useRef(null); // canonical Twilio Device (once loaded)
  const twilioRef = useRef(null); // { Device } from @twilio/voice-sdk

  const refreshDevices = useCallback(async () => {
    const { inputs, outputs } = await enumerateAudioDevices();
    setInputs(inputs);
    setOutputs(outputs);
    return { inputs, outputs };
  }, []);

  /**
   * enable() — opt-in activation.  Runs when the operator explicitly
   * clicks the "Use Browser Headset" button.  Never runs on login.
   */
  const enable = useCallback(async () => {
    setState(STATE_CONNECTING);
    setError('');
    try {
      // 1. Microphone permission — requested ONLY when opting in.
      let stream = null;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch (e) {
        throw new Error('Microphone permission was denied.');
      }
      // Release the probe immediately; Twilio Device will re-acquire.
      try { stream.getTracks().forEach(t => t.stop()); } catch (_) {}

      // 2. Enumerate and pick preferred devices.
      const { inputs, outputs } = await refreshDevices();
      const pickMic    = pickPreferred(inputs,  loadPref(K_MIC));
      const pickOut    = pickPreferred(outputs, loadPref(K_OUTPUT));
      const pickRinger = pickPreferred(outputs, loadPref(K_RINGER));
      const nextSelected = {
        mic:    pickMic?.deviceId    || null,
        output: pickOut?.deviceId    || null,
        ringer: pickRinger?.deviceId || null,
      };
      setSelected(nextSelected);
      if (nextSelected.mic)    savePref(K_MIC,    nextSelected.mic);
      if (nextSelected.output) savePref(K_OUTPUT, nextSelected.output);
      if (nextSelected.ringer) savePref(K_RINGER, nextSelected.ringer);

      // 3. Backend access token — endpoint must exist server-side.
      const tokenRes = await api.get('/api/telephony/browser-token').catch((e) => e?.response);
      if (!tokenRes || tokenRes.status !== 200 || !tokenRes.data?.token) {
        throw new Error('Browser Headset backend not configured yet.');
      }

      // 4. Lazy-load the Twilio Voice SDK (never bundled on Standard mode).
      let TwilioMod;
      try {
        TwilioMod = await import(/* webpackChunkName: "twilio-voice" */ '@twilio/voice-sdk');
      } catch (e) {
        throw new Error('Twilio Voice SDK not installed on this build.');
      }
      const { Device } = TwilioMod;
      twilioRef.current = TwilioMod;

      // 5. Instantiate exactly ONE canonical Device for this session.
      if (deviceRef.current) {
        try { await deviceRef.current.destroy(); } catch (_) {}
        deviceRef.current = null;
      }
      const dev = new Device(tokenRes.data.token, { logLevel: 'error' });
      deviceRef.current = dev;
      // Apply audio preferences.
      try {
        if (nextSelected.mic    && dev.audio?.setInputDevice)          await dev.audio.setInputDevice(nextSelected.mic);
        if (nextSelected.output && dev.audio?.speakerDevices?.set)     await dev.audio.speakerDevices.set([nextSelected.output]);
        if (nextSelected.ringer && dev.audio?.ringtoneDevices?.set)    await dev.audio.ringtoneDevices.set([nextSelected.ringer]);
      } catch (_) { /* non-fatal */ }
      // Register for inbound only when browser mode is active.
      try { await dev.register(); } catch (_) { /* non-fatal */ }

      // v3.21-1754 · Register the canonical Device in the module-scope
      // singleton so `useOutboundCall` (and any other outbound entry
      // point) can consult it without duplicating construction.  The
      // singleton also attaches a `devicechange` listener that fires
      // the callback below on any headset unplug / reconnect —
      // WITHOUT requiring the operator to open Audio Settings.
      setActiveDevice(dev, {
        onDeviceChange: async () => {
          const { inputs: newInputs, outputs: newOutputs } = await refreshDevices();
          // Re-check that the saved preferences are still available.
          const savedMic    = loadPref(K_MIC);
          const savedOut    = loadPref(K_OUTPUT);
          const savedRinger = loadPref(K_RINGER);
          const micStillHere    = !savedMic    || newInputs.some(d => d.deviceId === savedMic);
          const outStillHere    = !savedOut    || newOutputs.some(d => d.deviceId === savedOut);
          const ringerStillHere = !savedRinger || newOutputs.some(d => d.deviceId === savedRinger);
          if (!micStillHere || !outStillHere || !ringerStillHere) {
            // Fall back safely to default without ending the active call.
            try {
              if (!micStillHere && dev.audio?.setInputDevice)          await dev.audio.setInputDevice('default');
              if (!outStillHere && dev.audio?.speakerDevices?.set)     await dev.audio.speakerDevices.set(['default']);
              if (!ringerStillHere && dev.audio?.ringtoneDevices?.set) await dev.audio.ringtoneDevices.set(['default']);
            } catch (_) { /* non-fatal */ }
            toast.message('Headset disconnected. Audio moved to default.');
          } else if (savedMic || savedOut || savedRinger) {
            // Preferred devices reappeared — restore only when no call is active.
            const noActiveCall = !dev.calls || dev.calls.length === 0 ||
              (typeof dev.calls === 'object' && Array.from(dev.calls || []).length === 0);
            if (noActiveCall) {
              try {
                if (savedMic    && dev.audio?.setInputDevice)          await dev.audio.setInputDevice(savedMic);
                if (savedOut    && dev.audio?.speakerDevices?.set)     await dev.audio.speakerDevices.set([savedOut]);
                if (savedRinger && dev.audio?.ringtoneDevices?.set)    await dev.audio.ringtoneDevices.set([savedRinger]);
              } catch (_) { /* non-fatal */ }
            }
          }
        },
      });

      setState(STATE_ACTIVE);
      return { ok: true };
    } catch (e) {
      setError(e?.message || 'Browser Headset unavailable.');
      setState(STATE_ERROR);
      return { ok: false, error: e?.message };
    }
  }, [refreshDevices]);

  /**
   * disable() — return to Standard Calling.  Never destroys unrelated
   * telephony state; only the browser Device is torn down.
   */
  const disable = useCallback(async () => {
    // v3.21-1754 · Clear the module-scope singleton FIRST so any
    // in-flight outbound call attempt sees no active browser device
    // and safely rolls back to Standard.
    clearActiveDevice();
    try { await deviceRef.current?.unregister?.(); } catch (_) {}
    try { await deviceRef.current?.destroy?.(); } catch (_) {}
    deviceRef.current = null;
    twilioRef.current = null;
    setState(STATE_IDLE);
    setError('');
  }, []);

  const setMic    = useCallback((id) => { setSelected(s => ({ ...s, mic: id })); savePref(K_MIC, id); }, []);
  const setOutput = useCallback((id) => { setSelected(s => ({ ...s, output: id })); savePref(K_OUTPUT, id); }, []);
  const setRinger = useCallback((id) => { setSelected(s => ({ ...s, ringer: id })); savePref(K_RINGER, id); }, []);

  /** Local Test Headset — never places a call. */
  const testHeadset = useCallback(async () => {
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      const ctx = new AC();
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = 440;
      const gain = ctx.createGain();
      gain.gain.value = 0.05;
      osc.connect(gain).connect(ctx.destination);
      osc.start();
      setTimeout(() => { try { osc.stop(); ctx.close(); } catch (_) {} }, 600);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e?.message };
    }
  }, []);

  return {
    state, error,
    isConnecting: state === STATE_CONNECTING,
    isActive:     state === STATE_ACTIVE,
    isError:      state === STATE_ERROR,
    inputs, outputs, selected,
    enable, disable,
    setMic, setOutput, setRinger,
    refreshDevices, testHeadset,
    device: () => deviceRef.current,
  };
}
